// Shared by the client reporter and the log-client-error route so both sides agree on what is
// worth an entry in Cloud Logging. Dropping a report client-side also removes the POST — which is
// itself a Cloud Run request log — so the filter is applied at both ends deliberately.
//
// Measured over 24h of production client-exceptions, the patterns below accounted for ~27% of all
// reports and carried no actionable signal:
//   - "Script error." is the cross-origin stub: no message, no usable stack, by definition.
//   - jQuery/$/googletag/LiveChatWidget are injected third-party or extension scripts, not our code.
//   - removeChild/insertBefore/parentNode failures are almost always extensions mutating the DOM
//     under React (same family as $RC, Next's streaming-hydration patch, hitting a node an
//     extension already removed).
const NOISE_PATTERNS: RegExp[] = [
  /^script error\.?$/i,
  /\b(jQuery|googletag|LiveChatWidget|fbq|gtag)\b.*\bis not defined\b/i,
  /\$ is not defined/i,
  /Failed to execute '(removeChild|insertBefore)' on 'Node'/i,
  /Cannot read properties of null \(reading 'parentNode'\)/i,
  /\b(chrome|moz|safari)-extension:\/\//i,
  // Storage is unavailable in private mode and some in-app webviews. This is an environment
  // condition, not a defect, and it repeats on every page load for the affected visitor.
  /Failed to read the 'localStorage' property/i,
  /Access is denied for this document/i,
  // WebKit's generic fetch-failure stub (Safari's equivalent of "Script error.") — no origin,
  // no distinguishing detail, true for everything from a dropped connection to an ad blocker.
  /^load failed\.?$/i,
  // IndexedDB teardown error from a browser extension (password manager, ad blocker) or private
  // browsing's storage shim, not our code — this app does not use IndexedDB anywhere.
  /Attempt to delete range from database without an in-progress transaction/i,
  // Legacy Edge/IE artifact from the browser's own postMessage bridge to its UI shell; the
  // Id/ParamCount are instance-specific, never ours to fix.
  /Object Not Found Matching Id:\d+, MethodName:\w+, ParamCount:\d+/i,
  // Chrome's own benign timing notice, not a failure — ResizeObserver coalesces the overflow
  // into the next frame regardless, and dozens of unrelated libraries (incl. Radix, Swiper) trip it.
  /ResizeObserver loop (completed with undelivered notifications|limit exceeded)/i,
]

export const isNoisyClientError = (message: string | undefined): boolean =>
  !message || NOISE_PATTERNS.some((re) => re.test(message))
